import React, { useReducer } from "react";
import Dayjs from "dayjs";
import { useParams } from "react-router-dom";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";

// Custom.
import PreviewLayout from "containers/Layout/PreviewLayout";
import { Form, FormTabProps } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_TUTOR_CERTIFICATE,
  UPDATE_TUTOR_CERTIFICATE,
  CREATE_TUTOR_CERTIFICATE,
  SEARCH_GROUP,
} from "api";
import { VoAuth, regexPatterns, GeneralObject } from "@vocollege/app";
import { reducer } from "@vocollege/app/dist/modules/VoHelpers";
import { useStyles } from "ui/styles/form";
import TutorCertificatePreview from "containers/Templates/TutorCertificate/TutorCertificatePreview";
import { SEARCH_USER } from "@vocollege/app/dist/modules/VoApi";
import { FormProps } from "@vocollege/components/dist/Form";
import { getUserString } from "@vocollege/app/dist/modules/VoHelpers";
import BatchSelection from "components/BatchSelection";
import BatchPersonList, {
  BatchPersonListItem,
} from "components/BatchPersonList";
import {
  getPageTitle,
  getTutorCertificateModules,
  getGroupTitle,
  getGroupTitleValue,
} from "services/Utils";
import SaveConsentDialog from "components/SaveConsentDialog";
import { EnhancedTableStatusCell } from "@vocollege/components";

const initialFormState = {
  formLoading: true,
  formState: {
    isDirty: false,
    isValid: false,
  },
  mode: "single",
  creditsHelperText: I18n.get.diploma.helpTexts.credits.ung,
};

const TutorCertificateForm: React.FC = () => {
  const initialDate = Dayjs().format("YYYY-MM-DD");
  const urlParams = useParams<any>();
  const { id } = urlParams;
  const classes = useStyles();
  const navigate = useNavigate();
  const [formState, formDispatch] = useReducer(reducer, initialFormState);

  // Methods.

  const getInitialState = () => {
    const initialState: { [key: string]: any } = {
      id: 0,
      firstname: "",
      lastname: "",
      personal_number: "",
      date: initialDate,
      author: null,
      responsible: "",
      responsible2: "",
      region: null,
      local_id: null,
      educator_id: null,
      steps: [],
      step2: getTutorCertificateModules(2).map((v: GeneralObject) => v.name),
      done: 0,
      list: [],
      scope: "",
    };
    if (id && id === "skapa") {
      initialState.author = {
        id: VoAuth.currentUser.id,
        name: VoAuth.currentUser.name,
        type: "user",
      };
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(reducer, getInitialState());

  const isMultiple = () => {
    return formState.mode === "multiple";
  };

  const handleCancel = () => {
    navigate("/handledarintyg");
  };

  const handleFormChange: FormProps["onFormChange"] = (formState) => {
    formDispatch({ key: "formState", values: formState });
  };

  const handleComplete = (obj: any) => {
    if (isMultiple()) {
      navigate("/handledarintyg");
    } else {
      navigate(`/handledarintyg/${obj[Object.keys(obj)[0]].id}`);
    }
  };

  const handleBatchPersonListChange = (items: BatchPersonListItem[]) => {
    dispatch({
      values: {
        firstname: items[0].firstname,
        lastname: items[0].lastname,
        personal_number: items[0].personal_number,
        list: items,
      },
    });
  };

  const getExtraActions = () => {
    return (
      isMultiple() && (
        <>
          <BatchPersonList
            className="vo-batch-person-list-button"
            onChange={handleBatchPersonListChange}
          />
          <div className={classes.grow}></div>
        </>
      )
    );
  };

  const handleStepChange = (value: string, step: number) => {
    dispatch({ key: `step2`, values: value });
  };

  const getAvailableLocalValues = (data: any, state: any) => {
    let values = [
      {
        label: I18n.get.misc.nothing,
        value: "0",
      },
    ];
    if (state && state.region) {
      values = values.concat(
        state.region.groups
          .filter((v: any) => {
            // This check should actually be done in the backend, but for some reason
            // User::hasPermission() doesn't work in Group::groups().
            if (VoAuth.ability.cannot("view_all", "Groups") && v.status === 0) {
              return false;
            }
            return v.category === "local";
          })
          .map((v: any) => ({
            label: getGroupTitleValue(v),
            value: v.id,
          }))
      );
    }
    return values;
  };

  const getAvailableEducatorValues = (data: any, state: any) => {
    let values = [
      {
        label: I18n.get.misc.nothing,
        value: "0",
      },
    ];

    if (state && state.region && state.local_id && state.local_id !== "0") {
      let local = state.region.groups.find((v: any) => state.local_id === v.id);
      if (local) {
        values = values.concat(
          local.groups
            .filter((v: any) => {
              // This check should actually be done in the backend, but for some reason
              // User::hasPermission() doesn't work in Group::groups().
              if (
                VoAuth.ability.cannot("view_all", "Groups") &&
                v.status === 0
              ) {
                return false;
              }
              return v.category === "educator";
            })
            .map((v: any) => ({
              label: getGroupTitleValue(v),
              value: v.id,
            }))
        );
      }
    }

    return values;
  };

  const getTabs = () => {
    const tabs: FormTabProps[] = [];
    tabs.push({
      key: "content",
      label: I18n.get.misc.content,
      fields: [
        {
          name: "list",
          label: "",
          type: "custom",
          grid: {
            xs: 12,
          },
          render: (data: any) => <></>,
          overrideValue: state.list,
        },
        {
          name: "personal_number",
          label: I18n.get.user.labels.personalNumber,
          type: !isMultiple() ? "text" : "hidden",
          grid: {
            xs: 7,
          },
          params: {
            helperText: I18n.get.form.messages.personalNumberWhenPossible,
          },
          validation: {
            validate: (value: any) => {
              if (!value || value === "") {
                return true;
              }
              return (
                !!value.match(regexPatterns.personalNumber) ||
                I18n.get.form.errors.personalNumberInvalid
              );
            },
          },
          onChange: (value: any) =>
            dispatch({ key: "personal_number", values: value }),
        },
        {
          name: "date",
          label: I18n.get.misc.date,
          type: "date_field",
          required: true,
          grid: {
            xs: 5,
          },
          onChange: (value: any) => dispatch({ key: "date", values: value }),
        },
        {
          name: "firstname",
          label: I18n.get.user.labels.firstname,
          type: !isMultiple() ? "text" : "hidden",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          validation: {
            required: !isMultiple(),
          },
          onChange: (value: any) =>
            dispatch({ key: "firstname", values: value }),
        },
        {
          name: "lastname",
          label: I18n.get.user.labels.lastname,
          type: !isMultiple() ? "text" : "hidden",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          validation: {
            required: !isMultiple(),
          },
          onChange: (value: any) =>
            dispatch({ key: "lastname", values: value }),
        },
        {
          name: "steps",
          label: I18n.get.tutorCertificate.labels.steps,
          type: "checkboxes",
          grid: {
            xs: 12,
            md: state.steps.indexOf("2") > -1 ? 6 : 12,
          },
          onChange: (value: any) => dispatch({ key: "steps", values: value }),
          params: {
            row: true,
            availableValues: [
              {
                name: "1",
                label: "1",
              },
              {
                name: "2",
                label: "2",
              },
              {
                name: "3",
                label: "3",
              },
            ],
          },
        },
      ],
    });

    if (state.steps.indexOf("2") > -1) {
      tabs[0].fields.push({
        name: "step2",
        label: I18n.get.tutorCertificate.labels.step2Modules,
        type: "checkboxes",
        grid: {
          xs: 12,
          md: 6,
        },
        onChange: (value: any) => handleStepChange(value, 2),
        params: {
          // row: true,
          availableValues: getTutorCertificateModules(2),
        },
      });
    }

    tabs[0].fields.push(
      {
        name: "region",
        label: I18n.get.group.categories.region,
        type: "entity_field",
        required: true,
        validation: {
          required: I18n.get.form.errors.required,
        },
        grid: {
          xs: 12,
        },
        params: {
          renderFieldValue: getGroupTitleValue,
          dialog: {
            types: ["group"],
            renderTitleField: getGroupTitle,
            extraDetails: (item: any) =>
              item?.status === 0 && (
                <EnhancedTableStatusCell data={item.status} />
              ),
            query: SEARCH_GROUP,
            category: "searchGroup",
            variables: {
              categories: ["region"],
            },
          },
        },
        onChange: (value: any) => dispatch({ key: "region", values: value }),
      },

      {
        name: "local_id",
        label: I18n.get.group.categories.local,
        type: "select",
        grid: {
          xs: 12,
        },
        params: {
          availableValues: getAvailableLocalValues,
          dependency: {
            region: {
              value: null,
              visibility: false,
            },
          },
        },
        onChange: (value: any) => dispatch({ key: "local_id", values: value }),
      },
      {
        name: "educator_id",
        label: I18n.get.group.categories.educator,
        type: "select",
        grid: {
          xs: 12,
        },
        params: {
          availableValues: getAvailableEducatorValues,
          dependency: {
            local_id: {
              value: "0",
              visibility: false,
            },
          },
        },
        onChange: (value: any) =>
          dispatch({ key: "educator_id", values: value }),
      },

      {
        name: "responsible",
        label: I18n.get.tutorCertificate.template.responsible,
        type: "text",
        required: true,
        validation: {
          required: true,
        },
        grid: {
          xs: 12,
        },
        onChange: (value: any) =>
          dispatch({ key: "responsible", values: value }),
        params: {
          inputProps: {
            autoComplete: "on",
          },
        },
      },
      {
        name: "responsible2",
        label: I18n.get.tutorCertificate.template.responsible,
        type: "text",
        grid: {
          xs: 12,
        },
        onChange: (value: any) =>
          dispatch({ key: "responsible2", values: value }),
        params: {
          inputProps: {
            autoComplete: "on",
          },
        },
      },
      {
        name: "scope",
        label: I18n.get.tutorCertificate.labels.scope,
        type: "text",
        grid: {
          xs: 12,
        },
        onChange: (value: any) => dispatch({ key: "scope", values: value }),
        params: {
          helperText: I18n.get.tutorCertificate.messages.scope,
          inputProps: {
            autoComplete: "on",
          },
        },
      },
      {
        name: "author",
        label: I18n.get.misc.author,
        type: "entity_field",
        grid: {
          xs: 12,
        },
        params: {
          dialog: {
            renderTitleField: (item: any) => getUserString(item),
            types: ["user"],
            query: SEARCH_USER,
            category: "searchUser",
          },
          fields: {
            user: {
              title: "name",
            },
          },
        },
      }
    );

    return tabs;
  };

  return (
    <>
      <PreviewLayout
        className={classes.root}
        leftColumn={
          <>
            <Alert severity="warning" className={classes.row}>
              {I18n.get.vocertMessages.correctPreview}
            </Alert>
            {!state.uid && !formState.formLoading && (
              <Alert severity="error" className={classes.row}>
                {I18n.get.diploma.messages.saveBeforePrint}
              </Alert>
            )}
            {!formState.formLoading && id && id === "skapa" && (
              <BatchSelection
                onChange={(tab: any) =>
                  formDispatch({ key: "mode", values: tab })
                }
              />
            )}
            <Form
              tabs={getTabs()}
              labels={{
                loading: I18n.get.form.labels.loadingContent,
                create: I18n.get.tutorCertificate.labels.createTutorCertificate,
                created: isMultiple()
                  ? I18n.get.tutorCertificate.messages
                      .tutorCertificatesGenerated
                  : I18n.get.tutorCertificate.messages.tutorCertificateSaved,
                updated:
                  I18n.get.tutorCertificate.messages.tutorCertificateSaved,
                deleted:
                  I18n.get.tutorCertificate.messages.tutorCertificateDeleted,
                fieldRequired: I18n.get.form.errors.required,
              }}
              operations={{
                category: "certTutorCertificate",
                get: GET_TUTOR_CERTIFICATE,
                create: CREATE_TUTOR_CERTIFICATE,
                update: UPDATE_TUTOR_CERTIFICATE,
              }}
              paths={{
                back: "/handledarintyg",
              }}
              initialState={getInitialState()}
              renderPageTitle={(state) =>
                !isMultiple()
                  ? getPageTitle(
                      state,
                      I18n.get.tutorCertificate.labels.createTutorCertificate
                    )
                  : ""
              }
              urlParams={urlParams}
              createParam="skapa"
              classes={{
                toolbar: classes.toolbar,
                paper: classes.form,
                paperRoot: "vo-batch-person-list-form",
              }}
              onFormChange={handleFormChange}
              onQueryLoading={(loading) =>
                formDispatch({ key: "formLoading", values: loading })
              }
              onDataChange={(data, fetchedData) => {
                dispatch({
                  values: {
                    ...data,
                    uid: fetchedData.certTutorCertificate.uid,
                  },
                });
              }}
              onComplete={handleComplete}
              onCancel={handleCancel}
              loadQueryOnParamsChange
              toolbarProps={{
                options: {
                  saveButton: {
                    label: isMultiple()
                      ? I18n.get.vocertActions.generate
                      : null,
                  },
                },
                extraActions: getExtraActions(),
              }}
            />
          </>
        }
        rightColumn={
          !formState.formLoading ? (
            <TutorCertificatePreview
              item={state}
              formState={formState.formState}
              id={id}
            />
          ) : (
            <></>
          )
        }
        rightColumnProps={{
          className: clsx(classes.previewWrapper),
        }}
      />
      {id === "skapa" && <SaveConsentDialog context="TutorCertificate" />}
    </>
  );
};

export default TutorCertificateForm;
